import {
  CSSProperties,
  JSX,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import i18next from 'i18next';

import FotoscapeLink from '../../../../../../components/FotoscapeLink/FotoscapeLink';
import { setUpAds } from '../../utils';
import {
  INTEREST_URL,
  LOOKBOOK_ROUTE_PATH,
} from '../../../../router/constants';
import { CATEGORY_ROUTE, INTERESTS_ROUTE } from '../../../../router/router';
import { IDailyLookBook } from '../../../../services/api/lookBook/lookbook-model-definitions.ts';
import {
  getInterestBackgroundImg,
  getWithOutSpecialChars,
} from '../../../../utils';
import { LOOKBOOK_PARAMETERS_TO_COPY } from '../../../Lookbook/contants';
import {
  LATEST_DESKTOP_AD_MAP_DATA,
  LATEST_MOBILE_AD_MAP_DATA,
  LATEST_SMALL_TABLED_AD_MAP_DATA,
  LATEST_TABLED_AD_MAP_DATA,
  TRAVEL_DESKTOP_AD_MAP_DATA,
  TRAVEL_MOBILE_AD_MAP_DATA,
  TRAVEL_SMALL_TABLED_AD_MAP_DATA,
  TRAVEL_TABLED_AD_MAP_DATA,
} from './constants';
import InterestCarousel from '../../../../components/InterestCarousel/InterestCarousel';
import Footer from '../../../../../../components/Footer/Footer';
import InterestGallery from '../../../../components/InterestGallery/InterestGallery';
import PressCard from '../../components/PressCard/PressCard';
import { useResize } from '../../../../hooks';
import CardSkeleton from './components/CardSkeleton/CardSkeleton';
import { cardSize } from './components/CardSkeleton/interfaces';
import { getSSRTranslation } from '@/i18n.ts';
import { IAdParameter } from '../../interfaces';
import Lazy from '../../../../components/Lazy/Lazy';
import CoverCard from './components/CoverCard/CoverCard';
import SingleCard from '../../components/SingleCard/SingleCard';
import { CommonPageDataContext } from '../../../../ContextWrapper/WithInterest/CommonPageDataContext.ts';
import Icon from '../../../../../../components/Icon/Icon.tsx';
import { IconTypes } from '@components/Icon/constants.ts';

import './index.scss';
import { useQuery } from '@tanstack/react-query';
import {
  fetchSchedule,
  HOME_QUERY_KEYS,
} from '@moment/net/classic-fotoscapes/fetch-home.ts';
import { loadAdScripts } from '@pages/Root/utils/ads.ts';
import { isDntEnabled } from '@utils/dnt.ts';
import {
  HERO_SCHEDULE,
  INTEREST_SCHEDULE,
  LATEST_SCHEDULE,
  POPULAR_SCHEDULE,
} from '../../constants.ts';
import { useAnalytics } from '@moment/analytics/AnalyticsProvider.tsx';
import { useAds } from '@moment/context/AdsContext.tsx';
import { getCurrentNumberOfInteractions } from '@moment/local-personalization/scorer.ts';

const LATEST_AD_ID = 'latest';

const Home: () => JSX.Element = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [searchParams] = useSearchParams();
  const { isTabled, isMobile, isDesktop } = useResize();
  const { interests } = useContext(CommonPageDataContext);

  const { adConfigData } = useAds();

  const analytics = useAnalytics();
  const context = useContext(CommonPageDataContext);

  const { data: heroData, isLoading: isHeroLoading } = useQuery({
    queryKey: [...HOME_QUERY_KEYS.hero, lang],
    queryFn: fetchSchedule({lang}, HERO_SCHEDULE),
  });

  const { data: interestData, isLoading: isInterestLoading } = useQuery({
    queryKey: [...HOME_QUERY_KEYS.interest, lang],
    queryFn: fetchSchedule({lang}, INTEREST_SCHEDULE.NZf7cr),
  });

  const { data: latestData, isLoading: isLatestLoading } = useQuery({
    queryKey: [...HOME_QUERY_KEYS.latest, lang],
    queryFn: fetchSchedule({lang}, LATEST_SCHEDULE),
  });

  const { data: popularData, isLoading: isPopularLoading } = useQuery({
    queryKey: [...HOME_QUERY_KEYS.popular, lang],
    queryFn: fetchSchedule({lang}, POPULAR_SCHEDULE),
  });


  const interestTravelSectionType = useMemo(() => {
    const TRAVEL_UID = 'NZf7cr';
    const interest = interests ? interests.interestForId(TRAVEL_UID) : null;
    const backgroundImg = interest ? getInterestBackgroundImg(interest) : '';

    return {
      uid: TRAVEL_UID,
      backgroundImage: backgroundImg,
    };
  }, [interests]);

  const galleryStyleList = useMemo(() => {
    return {
      '--background-image': `url(${interestTravelSectionType.backgroundImage})`,
    } as CSSProperties;
  }, [interestTravelSectionType]);

  const galleryClassList = useMemo(() => {
    return `main-galleries__interest interest-background interest-background--with-img interest-background--${interestTravelSectionType.uid}`;
  }, [interestTravelSectionType]);

  const getInterestName = useCallback(
    (interestUID: string) => {
      const name = interests.interestForId(interestUID)?.name;

      if (name) {
        return getSSRTranslation(name, i18n);
      }

      return '';
    },
    [interests]
  );

  const { heroList, travelList, latestList, popularList } = useMemo(() => {
    return {
      heroList: heroData?.items ?? [],
      travelList: interestData?.items ?? [],
      latestList: latestData?.items ?? [],
      popularList: popularData?.items ?? [],
    };
  }, [heroData, interestData, latestData, popularData]);

  const mainCoverData = useMemo(() => {
    if (heroList.length) {
      const { previews, summary, title, uid, interests } = heroList[0];

      return {
        uid,
        images: previews,
        summary: getSSRTranslation(summary, i18n),
        title: getSSRTranslation(title, i18n),
        titleData: title,
        interestName: getInterestName(interests[0]),
        interestId: interests[0],
      };
    }

    return null;
  }, [heroList, getInterestName]);

  const availableTravelList = useMemo(() => {
    return travelList.slice(0, isTabled ? 2 : 3);
  }, [travelList, isTabled]);

  const availableLatestList = useMemo(() => {
    return isDesktop ? latestList.slice(0, 3) : latestList.slice(0, 4);
  }, [isDesktop, latestList]);

  const getPressCardData = ({
    title,
    previews,
    uid,
    interests,
  }: IDailyLookBook) => {
    return {
      interests,
      title: title,
      uid,
      subtitle: getInterestName(interests[0]),
      imageList: previews,
    };
  };

  const renderTravelAd = useCallback(() => {
    const size = [
      ...TRAVEL_DESKTOP_AD_MAP_DATA.adSizeList,
      ...TRAVEL_TABLED_AD_MAP_DATA.adSizeList,
      ...TRAVEL_SMALL_TABLED_AD_MAP_DATA.adSizeList,
      ...TRAVEL_MOBILE_AD_MAP_DATA.adSizeList,
    ];

    const responsiveSize = [
      TRAVEL_DESKTOP_AD_MAP_DATA,
      TRAVEL_TABLED_AD_MAP_DATA,
      TRAVEL_SMALL_TABLED_AD_MAP_DATA,
      TRAVEL_MOBILE_AD_MAP_DATA,
    ];

    const parameterList: IAdParameter[] = [];

    for (const parameter of LOOKBOOK_PARAMETERS_TO_COPY) {
      const parameterValue = searchParams.get(parameter);
      if (parameterValue) {
        parameterList.push({ parameter, parameterValue });
      }
    }
    parameterList.push({ parameter: 'pathname', parameterValue: 'home' });

    if (adConfigData) {
      setUpAds(adConfigData.adTag, 'travel', size, parameterList, responsiveSize);
    }
  }, [adConfigData, searchParams]);

  const renderLatestAd = useCallback(async () => {
    const size = [
      ...LATEST_DESKTOP_AD_MAP_DATA.adSizeList,
      ...LATEST_TABLED_AD_MAP_DATA.adSizeList,
      ...LATEST_SMALL_TABLED_AD_MAP_DATA.adSizeList,
      ...LATEST_MOBILE_AD_MAP_DATA.adSizeList,
    ];

    const responsiveSize = [
      LATEST_DESKTOP_AD_MAP_DATA,
      LATEST_TABLED_AD_MAP_DATA,
      LATEST_SMALL_TABLED_AD_MAP_DATA,
      LATEST_MOBILE_AD_MAP_DATA,
    ];

    const parameterList: IAdParameter[] = [];

    for (const parameter of LOOKBOOK_PARAMETERS_TO_COPY) {
      const parameterValue = searchParams.get(parameter);
      if (parameterValue) {
        parameterList.push({ parameter, parameterValue });
      }
    }
    parameterList.push({ parameter: 'pathname', parameterValue: 'home' });

    if (adConfigData) {
      if (!isDntEnabled()) await loadAdScripts(adConfigData.adTag);
      setUpAds(adConfigData.adTag, LATEST_AD_ID, size, parameterList, responsiveSize);
    }
  }, [adConfigData, searchParams]);

  useEffect(() => {
    if (heroList.length) {
      const mainView = heroList[0];

      const { title, uid, owner, interests } = mainView;
      analytics.emitPageViewEvent({
        numInteractions: getCurrentNumberOfInteractions(),
        lbtype: '',
        partnerId: context.partner || '',
        contentId: uid,
        source: window.location.href,
        language: i18next.language || i18next.resolvedLanguage || 'en',
        title: title.en,
        contentOwner: owner,
        primaryInterest: interests[0],
        searchParams,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroList[0]?.uid]);

  return (
    <div className="home">
      <main className="home__main">
        <section className="home__main-section">
          <div className="main-section__wrapper">
            <div className="main-section__content">
              <div className="main-section__content-item">
                {mainCoverData ? (
                  <CoverCard
                    interestId={mainCoverData.interestId}
                    lookbookUID={mainCoverData.uid}
                    imageList={mainCoverData.images}
                    title={mainCoverData.title}
                    description={mainCoverData.summary}
                    interestName={mainCoverData.interestName}
                  />
                ) : (
                  <CardSkeleton size={cardSize.large} />
                )}

                <div className="main-section__latest">
                  {isMobile ? (
                    <Lazy
                      className="ad-latest--mobile"
                      onRender={renderLatestAd}
                    >
                      <div id="ad_unit_latest"></div>
                    </Lazy>
                  ) : null}
                </div>
              </div>

              <div className="main-section__offers">
                {heroList[1] ? (
                  <SingleCard
                    link={LOOKBOOK_ROUTE_PATH.replace(
                      ':lookbookId',
                      heroList[1].uid
                    ).replace(
                      ':title?',
                      getWithOutSpecialChars(heroList[1].title.en)
                    )}
                    title={getSSRTranslation(heroList[1].title, i18n)}
                    description={getSSRTranslation(heroList[1].summary, i18n)}
                    descriptionLineCount={2}
                    logoURL={heroList[1].brandLogo}
                    imgList={heroList[1].previews}
                    interest={getInterestName(heroList[1].interests[0])}
                    interestId={heroList[1].interests[0]}
                    uid={heroList[1].uid}
                  />
                ) : (
                  <CardSkeleton />
                )}

                {heroList[2] ? (
                  <SingleCard
                    link={LOOKBOOK_ROUTE_PATH.replace(
                      ':lookbookId',
                      heroList[2].uid
                    ).replace(
                      ':title?',
                      getWithOutSpecialChars(heroList[2].title.en)
                    )}
                    title={getSSRTranslation(heroList[2].title, i18n)}
                    description={getSSRTranslation(heroList[2].summary, i18n)}
                    descriptionLineCount={2}
                    logoURL={heroList[2].brandLogo}
                    imgList={heroList[2].previews}
                    interest={getInterestName(heroList[2].interests[0])}
                    interestId={heroList[2].interests[0]}
                    uid={heroList[2].uid}
                  />
                ) : (
                  <CardSkeleton />
                )}
              </div>
            </div>
            <div className="main-section__latest">
              <h2 className="main-section__latest-header f-headline--xxxxl">
                {t('ourLatestPicks')}
              </h2>

              <div className="main-section__latest-container">
                {availableLatestList.length ? (
                  availableLatestList.map((item) => {
                    const { imageList, title, subtitle, uid, interests } =
                      getPressCardData(item);

                    return (
                      <PressCard
                        uid={uid}
                        interestId={interests[0]}
                        key={uid}
                        link={LOOKBOOK_ROUTE_PATH.replace(
                          ':lookbookId',
                          uid
                        ).replace(':title?', getWithOutSpecialChars(title?.en))}
                        title={getSSRTranslation(title, i18n)}
                        subtitle={subtitle}
                        imageList={imageList}
                      />
                    );
                  })
                ) : (
                  <>
                    <CardSkeleton size={cardSize.small} />
                    <CardSkeleton size={cardSize.small} />
                    <CardSkeleton size={cardSize.small} />
                    <CardSkeleton size={cardSize.small} />
                  </>
                )}
              </div>

              {isDesktop || isTabled ? (
                <Lazy onRender={renderLatestAd}>
                  <div className="ad-latest">
                    <div id="ad_unit_latest"></div>
                  </div>
                </Lazy>
              ) : null}
            </div>
          </div>
        </section>

        <section className="home__main-galleries">
          <div className={galleryClassList} style={galleryStyleList}>
            <div className="interest-background__item" />

            <div className="main-galleries__interest-header">
              <h2 className="main-galleries__interest-title f-headline--xxxxl">
                {t('travelGuides')}
              </h2>
              <FotoscapeLink
                className="main-galleries__next-button f-headline--s"
                to={
                  CATEGORY_ROUTE.path?.replace(
                    ':interestTitle',
                    INTEREST_URL.NZf7cr
                  ) as string
                }
              >
                {t('seeMore')}
                <Icon type={IconTypes.arrowRight} />
              </FotoscapeLink>
            </div>

            <div className="main-galleries__interest-content">
              {!!travelList.length &&
                availableTravelList.map(
                  ({ uid, previews, brandLogo, summary, title, interests }) => {
                    return (
                      <SingleCard
                        key={uid}
                        link={LOOKBOOK_ROUTE_PATH.replace(
                          ':lookbookId',
                          uid
                        ).replace(':title?', getWithOutSpecialChars(title.en))}
                        title={getSSRTranslation(title, i18n)}
                        description={getSSRTranslation(summary, i18n)}
                        descriptionLineCount={2}
                        logoURL={brandLogo}
                        imgList={previews}
                        interest={getInterestName(interests[0])}
                        interestId={interests[0]}
                        uid={uid}
                      />
                    );
                  }
                )}
            </div>

            <Lazy onRender={renderTravelAd}>
              <div
                className="main-galleries__interest-content-ad"
                id="ad_unit_travel"
              ></div>
            </Lazy>
          </div>

          <>
            <section className="main-galleries__calllery-wrapper">
              <h2 className="home__title f-headline--xxxxl">
                {t('popularGalleries')}
              </h2>

              <div className="main-galleries__carousel-wrapper">
                <InterestCarousel
                  isSmallView={true}
                  isMobile={isMobile}
                  itemList={popularList}
                  renderItem={({
                    previews,
                    title,
                    brandLogo,
                    uid,
                    interests,
                  }) => {
                    return (
                      <SingleCard
                        interestId={interests[0]}
                        uid={uid}
                        title={getSSRTranslation(title, i18n)}
                        logoURL={brandLogo}
                        imgList={previews}
                        link={LOOKBOOK_ROUTE_PATH?.replace(
                          ':lookbookId',
                          uid
                        ).replace(':title?', getWithOutSpecialChars(title?.en))}
                      />
                    );
                  }}
                />
              </div>
            </section>
          </>
        </section>

        <>
          <section className="home__interests">
            <h2 className="home__title f-headline--xxxxl">
              {t('discoverTopicsThatInterestYou')}
            </h2>

            <InterestGallery
              offsetTop={150}
              interestList={(interests ? interests.interests : []).slice(0, 6)}
            />

            <FotoscapeLink
              className="home__interests-button label--m"
              to={INTERESTS_ROUTE.path as string}
            >
              {t('more')}
            </FotoscapeLink>
          </section>
        </>
      </main>
      <Footer />
    </div>
  );
};

export default Home;
